import type {Repository} from '@github-ui/current-repository'
import {activityIndexPath} from '@github-ui/paths'
import {RefSelector} from '@github-ui/ref-selector'
import type {RefInfo} from '@github-ui/repos-types'
import {useNavigate} from '@github-ui/use-navigate'
import {UserSelector} from '@github-ui/user-selector'
import {TriangleDownIcon, TriangleUpIcon} from '@primer/octicons-react'
import {ActionList, Box, Link} from '@primer/react'
import {Octicon} from '@primer/react/deprecated'

import {useBaseUrl} from '../contexts/BaseUrlContext'
import {usePerPage} from '../contexts/PerPageContext'
import {useActivityAnalytics} from '../hooks/use-activity-analytics'
import {useActors} from '../hooks/use-actors'
import type {ActivityFilter} from '../types/activity-types'
import {ActivityTimeFilter} from './ActivityTimeFilter'
import {ActivityTypeFilter} from './ActivityTypeFilter'

export function ActivityFilterBar({
  repo,
  refInfo,
  filter,
}: {
  repo: Repository
  refInfo: RefInfo
  filter: ActivityFilter
}) {
  const {sendActivityClickEvent} = useActivityAnalytics()
  const baseUrl = useBaseUrl()
  const perPage = usePerPage()
  const {activityType, actor, timePeriod, sort} = filter
  const navigate = useNavigate()
  const actorsState = useActors({branch: refInfo.name, timePeriod: filter.timePeriod})
  return (
    <Box sx={{flexWrap: 'wrap', display: 'flex', flexGrow: 1, gap: 2, alignItems: 'center'}}>
      <RefSelector
        currentCommitish={refInfo.name || 'All branches'}
        defaultBranch={repo.defaultBranch}
        owner={repo.ownerLogin}
        repo={repo.name}
        canCreate={false}
        cacheKey={refInfo.listCacheKey}
        getHref={branch => activityIndexPath({repo, baseUrl, branch, filter, perPage})}
        hotKey="w"
        types={['branch']}
        hideShowAll={true}
        onOpenChange={open => open && sendActivityClickEvent('REF_SELECTOR_MENU')}
        onSelectItem={refName =>
          sendActivityClickEvent('REF_SELECTOR_MENU.VALUE', {
            ['default_branch']: refName === repo.defaultBranch,
          })
        }
        customFooterItemProps={{
          text: 'View activity for all branches',
          href: activityIndexPath({repo, baseUrl, filter, perPage}),
          onClick: () => {
            sendActivityClickEvent('REF_SELECTOR_MENU.VIEW_ALL')
          },
          sx: {alignItems: 'center', display: 'flex', justifyContent: 'center'},
        }}
      />
      <ActivityTypeFilter
        currentActivityType={activityType}
        onSelect={selectedActivityType => {
          if (selectedActivityType !== activityType) {
            navigate(
              activityIndexPath({
                repo,
                baseUrl,
                branch: refInfo.name,
                filter: {...filter, activityType: selectedActivityType},
                perPage,
              }),
            )
          }
        }}
      />
      <UserSelector
        defaultText="All users"
        width="medium"
        usersState={actorsState}
        currentUser={actor}
        onOpenChange={open => open && sendActivityClickEvent('ACTOR_FILTER')}
        onSelect={selectedUser => {
          if (selectedUser !== actor) {
            sendActivityClickEvent('ACTOR_FILTER.VALUE')
            navigate(
              activityIndexPath({
                repo,
                baseUrl,
                branch: refInfo.name,
                filter: {...filter, actor: selectedUser},
                perPage,
              }),
            )
          }
        }}
        renderCustomFooter={() => (
          <ActionList.LinkItem
            onClick={() => sendActivityClickEvent('ACTOR_FILTER.ALL')}
            href={activityIndexPath({
              repo,
              baseUrl,
              branch: refInfo.name,
              filter: {...filter, actor: undefined},
              perPage,
            })}
          >
            View activity for all users
          </ActionList.LinkItem>
        )}
      />
      <ActivityTimeFilter
        currentActivityTimePeriod={timePeriod}
        onSelect={selectedTimePeriod => {
          if (selectedTimePeriod !== timePeriod) {
            navigate(
              activityIndexPath({
                repo,
                baseUrl,
                branch: refInfo.name,
                filter: {...filter, timePeriod: selectedTimePeriod},
                perPage,
              }),
            )
          }
        }}
      />
      <Box sx={{marginLeft: 'auto'}}>
        {/* Clicking on this link will swap the sort order, so if the sort is currently ASC switch to DESC. Otherwise use ASC. */}
        <Link
          muted={true}
          onClick={() => sendActivityClickEvent('SORT_ORDER_LINK')}
          href={activityIndexPath({
            repo,
            baseUrl,
            branch: refInfo.name,
            filter: {...filter, sort: sort === 'ASC' ? 'DESC' : 'ASC'},
            perPage,
          })}
        >
          {sort === 'ASC' ? (
            <>
              Showing oldest first <Octicon icon={TriangleUpIcon} size={16} />
            </>
          ) : (
            <>
              Showing most recent first <Octicon icon={TriangleDownIcon} size={16} />
            </>
          )}
        </Link>
      </Box>
    </Box>
  )
}

try{ ActivityFilterBar.displayName ||= 'ActivityFilterBar' } catch {}